/**
 * Creazione nuova richiesta di preventivo (RDP).
 *
 * Flusso:
 * 1. Imposta data scadenza risposta (opzionale), note, valuta
 * 2. Cerca articoli dal magazzino e aggiungili con quantita
 * 3. Submit: crea la richiesta, poi aggiunge le righe
 * 4. Naviga al dettaglio della richiesta creata
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Label } from '@floraltech/ui';

interface Articolo {
  id: string;
  sku: string;
  descrizione: string;
  unita_misura: string | null;
}

interface RigaDaAggiungere {
  articolo_id: string;
  sku: string;
  descrizione: string;
  unita_misura: string | null;
  quantita: string;
}

/** Quantita pre-inserite dall'utente nella ricerca prima di aggiungere. */
type QtyMap = Record<string, string>;

export function NuovaRichiesta() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Dati richiesta
  const [dataScadenza, setDataScadenza] = useState('');
  const [note, setNote] = useState('');
  const [valuta, setValuta] = useState('EUR');

  // Ricerca articoli magazzino
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [articoliSearch, setArticoliSearch] = useState('');
  const [loadingArticoli, setLoadingArticoli] = useState(false);
  const [articoliQty, setArticoliQty] = useState<QtyMap>({});

  // Righe selezionate
  const [righe, setRighe] = useState<RigaDaAggiungere[]>([]);

  // Meta
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cerca articoli dal magazzino
  useEffect(() => {
    if (!articoliSearch.trim()) {
      setArticoli([]);
      return;
    }
    const fetchArticoli = async () => {
      setLoadingArticoli(true);
      const params = new URLSearchParams({ q: articoliSearch, limit: '50' });
      const res = await fetch(`/api/v1/magazzino/articoli?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setArticoli(Array.isArray(data) ? data : data.items ?? []);
      }
      setLoadingArticoli(false);
    };
    fetchArticoli();
  }, [articoliSearch, token]);

  const isRigaAggiunta = (articoloId: string) =>
    righe.some((r) => r.articolo_id === articoloId);

  const aggiungiRiga = (item: Articolo) => {
    if (isRigaAggiunta(item.id)) return;
    const qty = articoliQty[item.id] || '1';
    if (!qty || parseFloat(qty) <= 0) return;
    setRighe((prev) => [
      ...prev,
      {
        articolo_id: item.id,
        sku: item.sku,
        descrizione: item.descrizione,
        unita_misura: item.unita_misura,
        quantita: qty,
      },
    ]);
  };

  const rimuoviRiga = (articoloId: string) => {
    setRighe((prev) => prev.filter((r) => r.articolo_id !== articoloId));
  };

  const updateQuantita = (articoloId: string, quantita: string) => {
    setRighe((prev) =>
      prev.map((r) =>
        r.articolo_id === articoloId ? { ...r, quantita } : r,
      ),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (righe.length === 0) {
      setError('Aggiungi almeno un articolo');
      return;
    }

    setLoading(true);
    setError('');

    // 1. Crea richiesta
    const body: Record<string, unknown> = { valuta };
    if (dataScadenza) body.data_scadenza_risposta = dataScadenza;
    if (note.trim()) body.note = note.trim();

    const richiestaRes = await fetch('/api/v1/richieste', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!richiestaRes.ok) {
      const detail = await richiestaRes.json().catch(() => null);
      setError(detail?.detail || `Errore ${richiestaRes.status}`);
      setLoading(false);
      return;
    }

    const richiesta = await richiestaRes.json();

    // 2. Aggiungi righe
    for (const riga of righe) {
      await fetch(`/api/v1/richieste/${richiesta.id}/righe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          articolo_id: riga.articolo_id,
          codice_articolo: riga.sku,
          descrizione: riga.descrizione,
          unita_misura: riga.unita_misura,
          quantita: riga.quantita,
        }),
      });
    }

    navigate(`/richieste/${richiesta.id}`);
  };

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <h1 className="text-xl font-outfit font-semibold text-foreground">
        Nuova richiesta di preventivo
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dati richiesta */}
        <Card className="p-4 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">1. Dati richiesta</h2>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="data_scadenza">Scadenza risposta</Label>
              <Input
                id="data_scadenza"
                type="date"
                value={dataScadenza}
                onChange={(e) => setDataScadenza(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="valuta">Valuta</Label>
              <Input
                id="valuta"
                value={valuta}
                onChange={(e) => setValuta(e.target.value)}
                className="mt-1"
                placeholder="EUR"
              />
            </div>
            <div>
              <Label htmlFor="note">Note</Label>
              <Input
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1"
                placeholder="Annotazioni..."
              />
            </div>
          </div>
        </Card>

        {/* Ricerca articoli magazzino */}
        <Card className="p-4 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            2. Cerca articoli dal magazzino
          </h2>
          <Input
            placeholder="Cerca per SKU o descrizione..."
            value={articoliSearch}
            onChange={(e) => setArticoliSearch(e.target.value)}
          />
          {loadingArticoli ? (
            <p className="text-sm text-muted-foreground">Caricamento articoli...</p>
          ) : articoliSearch.trim() && articoli.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nessun articolo trovato
            </p>
          ) : articoli.length > 0 ? (
            <div className="max-h-72 overflow-y-auto border border-border rounded">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                  <tr className="border-b border-border">
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">SKU</th>
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Descrizione</th>
                    <th className="text-right px-3 py-1.5 text-muted-foreground font-medium w-24">Quantita</th>
                    <th className="px-3 py-1.5 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {articoli.map((item) => {
                    const added = isRigaAggiunta(item.id);
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-border ${added ? 'bg-green-50 dark:bg-green-900/10' : 'hover:bg-muted/30'}`}
                      >
                        <td className="px-3 py-1.5 font-mono text-xs">{item.sku}</td>
                        <td className="px-3 py-1.5">{item.descrizione || '—'}</td>
                        <td className="px-3 py-1.5">
                          {!added && (
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              value={articoliQty[item.id] ?? ''}
                              onChange={(e) =>
                                setArticoliQty((prev) => ({ ...prev, [item.id]: e.target.value }))
                              }
                              placeholder="Q.ta"
                              className="w-20 text-right text-sm ml-auto"
                            />
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {added ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => rimuoviRiga(item.id)}
                              className="text-destructive text-xs"
                            >
                              Rimuovi
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => aggiungiRiga(item)}
                              disabled={!articoliQty[item.id] || parseFloat(articoliQty[item.id]) <= 0}
                              className="text-xs"
                            >
                              Aggiungi
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>

        {/* Righe selezionate */}
        {righe.length > 0 && (
          <Card className="p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              3. Articoli selezionati ({righe.length})
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">SKU</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Descrizione</th>
                  <th className="text-right px-3 py-1.5 text-muted-foreground font-medium w-28">Quantita</th>
                  <th className="px-3 py-1.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <tr key={r.articolo_id} className="border-b border-border">
                    <td className="px-3 py-1.5 font-mono text-xs">{r.sku}</td>
                    <td className="px-3 py-1.5">{r.descrizione}</td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={r.quantita}
                        onChange={(e) => updateQuantita(r.articolo_id, e.target.value)}
                        className="text-right w-24 ml-auto"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => rimuoviRiga(r.articolo_id)}
                        className="text-destructive text-xs"
                      >
                        Rimuovi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={loading || righe.length === 0}>
            {loading ? 'Creazione...' : 'Crea richiesta'}
          </Button>
        </div>
      </form>
    </div>
  );
}
